/**
 * PlaylistRow — single row in the branded playlist tray.
 *
 * Desktop layout — flat dark card, just two columns now:
 *
 *   [ Avatar ] [ Title + Meta row ]                  [ NOW PLAYING ]
 *
 * The state pill (NOW PLAYING / UP NEXT) is absolutely positioned in
 * the top-right corner of the card on a single line, so the rest of
 * the card width belongs to Title + Meta. The legacy RightSide column
 * — which used to stack a wrapped 2-line state pill above a clip
 * duration in its own 34px-wide slot — is gone on desktop: it ate too
 * much horizontal real estate at HLTV's narrow ~236px rail width.
 * Clip duration is also dropped on desktop (the player chrome already
 * shows time / total).
 *
 * Mobile layout — 2x2 grid, every cell is its own atom:
 *
 *   [ Avatar  ] [ Title + meta row ]
 *   [ 00:31   ] [ ● NOW PLAYING    ]
 *
 * Duration tucks under the avatar at bottom-left, state pill sits at
 * bottom-right under the meta row. StatePill and DurationText flip
 * out of absolute / display:none and into explicit grid placements.
 *
 * The continuous-gradient-rails treatment from V1 is gone; rows are now
 * flat dark cards with visible vertical gap between them. The active row
 * is lifted by a soft accent fill (not a heavy gradient block).
 */
import { useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Box, Flex, Text } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";

import type { PlaylistClip } from "../../../../@types";
import {
  BrandedActiveClipIndexState,
  BrandedActiveClipState,
  BrandedLayoutVariantState,
} from "../../State";
import { buildMockMatchContext } from "../../mockData";
import {
  getClipLengthSec,
  getDisplayTags,
  getOpponentTeamLogo,
  getOpponentTeamName,
  getPlayerName,
  getPlayerPhoto,
  getRoundNumber,
  getTeamLogo,
  getTeamName,
  getViewCount,
} from "../../metadataHelpers";
import { composeBrandedTitle } from "../../titleComposer";
import { ChartIcon, PlayIcon, VsBadge } from "./icons";
import { TeamCrest } from "./TeamCrest";

interface PlaylistRowProps {
  clip: PlaylistClip;
  index: number;
  isActive: boolean;
  isUpNext: boolean;
}

/**
 * Per HLTV in-page embed proportions — the iframe lives in a narrow
 * column (~700px) so the rail is ~220-260px wide. Row height + typography
 * are tuned to that scale; rows looked clunky and the meta row spilled
 * past the right edge at the prior 74px/44px sizing.
 */
const ROW_HEIGHT = 60;

/**
 * Row card. Discrete rounded rectangle.
 *
 * Per latest design spec the row palette is a flat 2-tone system, not
 * the campaign-tinted color-mix gradients we were running earlier:
 *   - Border (both states): solid var(--row-bg) (deep brand navy)
 *   - Active background:    solid var(--row-bg) — same as the border,
 *                           so the active card reads as a filled brand
 *                           chip
 *   - Idle background:      transparent — rows sit directly on the
 *                           BrandedShell canvas with only the border
 *                           outlining them
 *
 * Hover keeps a subtle row-bg-tinted fill so non-active rows still
 * respond to pointer presence without breaking the flat-row aesthetic.
 *
 * Defaults via `var(--row-bg, #093358)` so the row still renders
 * correctly if a consumer instantiates a PlaylistRow outside the
 * BrandedShell scope (e.g. snapshot tests).
 */
const Row = styled(Flex)<{ $active: boolean; $bottomPlaylist?: boolean }>`
  align-items: center;
  background: ${({ $active }) =>
    $active ? "var(--row-bg, #093358)" : "transparent"};
  /* Border falls back through --row-border (a contrast-lifted color the
     shell only sets when the brand color is too dark to read against the
     canvas) → --row-bg → Allstar navy. Active fill stays on --row-bg so
     the selected row keeps the deep brand chip. */
  border: 1px solid var(--row-border, var(--row-bg, #093358));
  border-radius: 8px;
  cursor: pointer;
  flex: 0 0 auto;
  gap: 8px;
  /* 2px margin-right is the row-side gap inside the 9px iframe-edge
     gutter. Layout from row-right → iframe-right:
       2px (this margin)
       5px (scrollbar)
       2px (PlaylistColumn padding-right)
       = 9px total, scrollbar centered. Done via margin rather than the
     scroll region's padding-right so browser scrollbar quirks (overlay
     vs reserved) don't move the gap. */
  margin-right: 2px;
  min-height: ${ROW_HEIGHT}px;
  padding: 6px 8px;
  /* Anchor for the absolutely-positioned StatePill in the top-right
     corner. Without position: relative the pill would chase the next
     positioned ancestor up the tree (BrandedShell) and float in the
     wrong place. */
  position: relative;
  transition:
    background 120ms ease,
    border-color 120ms ease;

  &:hover {
    background: ${({ $active }) =>
      $active
        ? "var(--row-bg, #093358)"
        : "color-mix(in srgb, var(--row-bg, #093358) 55%, transparent)"};
  }

  /* Bottom-playlist variant: rows stack vertically and span the full
     iframe width — same [avatar | (title + meta)] horizontal layout as
     the desktop side-rail, but unconstrained on width so each row reads
     as a proper list item rather than a carousel card. Card grows a
     tick taller via the inner type bumps (TitleText / MetaRow), and the
     wider canvas means MetaRow no longer wraps. PlaylistColumn's bottom
     fade overlays the third row to cue scrollability. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    flex: 0 0 auto;
    width: 100%;
    gap: 12px;
    margin-right: 0;
    padding: 8px 12px;
  `}

  /* Mobile: per Figma 25-1097 the card flips from the desktop "row of
     three" layout to a 2x2 grid where every cell is its own atom:
        [ avatar  ] [ title + meta row ]
        [ 00:31   ] [ ● NOW PLAYING    ]
     Duration and state pill are separate elements in DIFFERENT cells —
     duration tucks under the avatar at bottom-left, state pill sits
     at bottom-right under the meta row. RightSide is collapsed via
     display: contents on mobile so its children participate in the
     grid directly. */
  @media (max-width: 560px) {
    display: grid;
    grid-template-columns: 28px 1fr;
    grid-template-rows: auto auto;
    column-gap: 10px;
    row-gap: 1px;
    /* End-align row 1 children so the avatar's bottom and the meta
       row's bottom both sit flush against the row-gap boundary —
       eliminates the leftover whitespace caused by ContentArea being
       shorter than the 28px mobile avatar. Row 2 items keep default
       start alignment via per-item align-self overrides. */
    align-items: end;
    flex: 0 0 220px;
    width: 220px;
    min-height: 0;
    margin-right: 0;
    padding: 5px;
  }
`;

/**
 * Headshot tile. HLTV bodyshots are tall portraits; we zoom + pixel-anchor
 * the face into the circle so the framing matches across portraits of
 * different aspect ratios. White ring on active, soft accent ring otherwise.
 *
 * When no `$src` is provided (clip metadata didn't include a playerPhoto),
 * the inner silhouette fallback inside <HeadshotFallback> renders so the
 * row never displays a hollow ring.
 */
const Headshot = styled(Box)<{
  $src?: string;
  $active: boolean;
  $bottomPlaylist?: boolean;
}>`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.45);
  ${({ $src }) =>
    $src
      ? `background-image: url(${$src});
         background-position: center 2px;
         background-repeat: no-repeat;
         background-size: 175% auto;`
      : ""}
  border: none;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 auto;
  /* Desktop: 30px headshot — was 36px, but the compact pill row + 226h
     iframe meant the avatar felt oversized next to the smaller meta
     text and title. Mobile keeps 28px since the touch-target case
     is unchanged. */
  height: 30px;
  justify-content: center;
  overflow: hidden;
  width: 30px;

  /* Bottom-playlist variant: avatar nudges up from 30 → 36 to match
     the wider 280px card. Stays in the horizontal flex layout — no
     grid placement needed since the bottom variant kept the desktop
     side-rail's row shape rather than the mobile 2x2 grid. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    height: 36px;
    width: 36px;
  `}

  @media (max-width: 560px) {
    grid-column: 1;
    grid-row: 1;
    /* Override grid-level align-items: end so the avatar sits flush
       at the TOP of the left column. Duration sits directly under
       it (align-self: start in row 2). */
    align-self: start;
    height: 28px;
    width: 28px;
  }
`;

/**
 * Silhouette glyph shown inside <Headshot> when partner-api hasn't
 * emitted a playerPhoto for the clip. Sized + tinted so it reads as a
 * neutral placeholder, never as missing/broken UI.
 */
const HeadshotFallback = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle cx="12" cy="9" r="4" fill="rgba(255,255,255,0.55)" />
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="rgba(255,255,255,0.55)" />
  </svg>
);

const ContentArea = styled(Flex)<{ $bottomPlaylist?: boolean }>`
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  min-width: 0;

  /* Bottom-playlist variant: stays in the desktop side-rail flex
     layout — just bumps the title/meta gap a tick to read well at the
     larger type sizes. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    gap: 4px;
    min-width: 0;
  `}

  /* Mobile: ContentArea occupies the TOP-RIGHT cell only (title + meta
     row). The state pill drops into the bottom-right cell as its own
     element, not nested inside ContentArea, so duration and state can
     sit on the same baseline as siblings of the avatar above them. */
  @media (max-width: 560px) {
    grid-column: 2;
    grid-row: 1;
    gap: 3px;
    min-width: 0;
  }
`;

const TitleText = styled(Text)<{ $bottomPlaylist?: boolean }>`
  color: #ffffff;
  /* Title sits just above the unified 10px meta register. 12px keeps it
     readable as the row's primary payload without dominating the card. */
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1px;
  line-height: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* Bottom-playlist variant: title bumped so the wider card doesn't
     feel underset. No padding-right hack — the third row column
     (<BPStatusColumn>) owns its own width, so the title's flex
     container has a predictable budget to ellipsize into. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 15px;
    line-height: 19px;
  `}
`;

const MetaRow = styled(Flex)<{ $bottomPlaylist?: boolean }>`
  align-items: center;
  color: #8fa9af;
  font-size: 10px;
  font-weight: 600;
  gap: 6px;

  /* Mobile: tighter gap between meta items to match the more compact
     card; gap halves from 6 → 3px. */
  @media (max-width: 560px) {
    gap: 3px;
  }

  /* Bottom-playlist variant: bump meta type and widen gap so the pill
     row breathes against the larger title above it. No padding-right
     hack — <BPStatusColumn> owns the right side as its own flex
     column. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 12px;
    gap: 8px;
    line-height: 16px;
  `}

  line-height: 12px;
  min-width: 0;
  /* Allow horizontal overflow to be hidden behind the right-side
     cluster — at narrow iframe widths the ROUND badge would otherwise
     spill into the state pill. Items closest to the title get clipped
     first via the order they appear in the DOM. */
  overflow: hidden;
`;

const ViewsCluster = styled(Flex)<{ $bottomPlaylist?: boolean }>`
  align-items: center;
  color: rgba(255, 255, 255, 0.85);
  /* Fixed size — views ("1.2m views") is the row's primary stat and
     must never ellipsize down to "2..." like it did at the earlier
     flex: 0 1 auto. When the meta row is too narrow for everything,
     MetaRow's overflow:hidden clips items toward the END of the row
     (ROUND badge first, then team pill) — views always renders in
     full. */
  flex: 0 0 auto;
  /* Smaller register than the other meta items (team pill, round
     badge) — views is a supporting stat, not a primary chip. CSS
     overrides the ChartIcon's intrinsic 10px so a single rule covers
     desktop + mobile without changing the JSX prop. */
  font-size: 8px;
  font-weight: 700;
  gap: 2px;
  white-space: nowrap;

  & svg {
    width: 9px;
    height: 9px;
  }

  /* Bottom-playlist variant: bump views font + glyph proportionally.
     Larger again so views reads at parity with the other meta chips
     under the wider card. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 12px;
    gap: 3px;
    & svg {
      width: 13px;
      height: 13px;
    }
  `}

  /* Mobile-only: views slides to the END of the meta row (after team
     pill and ROUND badge) per Figma 25-1097, and top-aligns to the
     row's top edge instead of vertically centering against the taller
     team/round pills. Size/gap match desktop now. */
  @media (max-width: 560px) {
    order: 3;
    align-self: flex-start;
  }
`;

/**
 * Trending-up chart glyph in front of the view count. Figma uses a flat
 * accent-colored chart icon (no chip, no circle) — same visual vocabulary
 * as the "Trending" pill in the header — so the meta row reads as a
 * pure-chip layout where ONLY the team pill and ROUND badge carry chip
 * chrome.
 */
const ViewsGlyph = styled(Box)`
  color: var(--accent, #3c87d2);
  display: inline-flex;
  flex: 0 0 auto;
`;

/**
 * Rounded pill enclosing `[crestA] vs [crestB]` — Figma treats the
 * matchup as a single chip, not three loose elements in a flex row.
 * Padding + border mirror the ROUND badge's recipe so the meta row
 * reads as a series of matched-weight chips.
 */
const TeamPill = styled(Flex)<{ $bottomPlaylist?: boolean }>`
  align-items: center;
  background: color-mix(
    in srgb,
    var(--accent, #3c87d2) 18%,
    var(--bg, #0c1318)
  );
  border: 1px solid color-mix(in srgb, var(--accent, #3c87d2) 45%, transparent);
  border-radius: 999px;
  flex: 0 0 auto;
  gap: 3px;
  height: 16px;
  padding: 0 5px;

  /* Bottom-playlist variant: pill scales up with the surrounding meta
     row. Per publisher review the previous +2pt bump still read as
     undersized against the wider row, so this iteration steps it up
     again so the team-vs-team chip is the clear primary meta atom. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    gap: 5px;
    height: 22px;
    padding: 0 9px;
  `}
`;

const VsInline = styled(Box)`
  color: rgba(255, 255, 255, 0.6);
  display: inline-flex;
  flex: 0 0 auto;
`;

/**
 * ROUND N pill at the end of the meta row. Filled with a darkened accent
 * mix so it reads as a campaign-tinted chip without competing with the
 * primary trending pill in the header. Hidden entirely when neither
 * partner-api nor mock data can produce a round number.
 */
/**
 * ROUND N pill. Recipe mirrors TeamPill so the meta row reads as a
 * matched set of accent-tinted chips, not a chip + a hard-edged badge.
 * Smaller text (8px) since the round number is supporting context, not
 * a primary stat.
 */
const RoundBadge = styled(Box)<{ $bottomPlaylist?: boolean }>`
  align-items: center;
  background: color-mix(
    in srgb,
    var(--accent, #3c87d2) 18%,
    var(--bg, #0c1318)
  );
  border: 1px solid color-mix(in srgb, var(--accent, #3c87d2) 45%, transparent);
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 8px;
  font-weight: 800;
  height: 16px;
  letter-spacing: 0.4px;
  padding: 0 5px;
  text-transform: uppercase;

  /* Bottom-playlist variant: bumped to match the bigger meta type.
     Tracks the TeamPill height + radius so the meta row reads as a
     matched set of large chips. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 11px;
    height: 22px;
    padding: 0 9px;
  `}
`;

/**
 * Combat tag chip (bottom-playlist only).
 *
 * Renders after the ROUND badge in MetaRow. Mirrors the TeamPill /
 * RoundBadge chip recipe (accent tint + rounded pill) so the meta row
 * reads as a matched chip set — no orphan visual languages.
 *
 * Sized to the bottom-playlist's larger meta scale; the side-rail and
 * mobile variants don't render these chips at all (the row's narrower
 * width can't budget extra atoms after team-vs-team + ROUND).
 */
const TagChip = styled(Box)`
  align-items: center;
  background: color-mix(
    in srgb,
    var(--accent, #3c87d2) 18%,
    var(--bg, #0c1318)
  );
  border: 1px solid color-mix(in srgb, var(--accent, #3c87d2) 45%, transparent);
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 800;
  height: 22px;
  letter-spacing: 0.4px;
  padding: 0 9px;
  text-transform: uppercase;
`;

/**
 * Shimmer keyframe — same recipe as ClipBadge.tsx so the bottom-
 * playlist ACE chip reads as the same atom as the match-history badge.
 * The slow 5s loop is deliberate: long enough that two ACE rows in
 * view don't shimmer in lockstep, fast enough that a viewer notices
 * the gleam within a typical clip-watch window.
 */
const aceShimmer = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;

/**
 * Gold ACE chip — mirrors the AceBadgeStyled treatment from
 * ClipBadge.tsx (the match-history badge): 135° gradient from #FFD700
 * (goldStar) to #e8a93e (timelineGold), navy900 text, subtle gold
 * text-shadow, plus the diagonal shimmer sweep. Implementation differs
 * slightly from ClipBadge — we animate a `transform: translateX` on
 * the ::after instead of background-position so the gleam is visible
 * across browsers without depending on background-size resolution
 * quirks. Same visual effect, more robust at the small chip scale.
 */
const AceTagChip = styled(TagChip)`
  background: linear-gradient(135deg, #ffd700, #e8a93e);
  border: 1px solid rgba(255, 215, 0, 0.65);
  color: #0b1116;
  overflow: hidden;
  position: relative;
  text-shadow: 0 1px 2px rgba(255, 215, 0, 0.2);

  &::after {
    animation: ${aceShimmer} 4s ease-in-out infinite;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.55) 50%,
      transparent 100%
    );
    content: "";
    height: 100%;
    left: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 40%;
  }
`;

/**
 * State pill (NOW PLAYING / UP NEXT).
 *
 * Desktop: absolutely positioned in the top-right corner of the Row on
 * a SINGLE line. Tiny font + tight letter-spacing so the label reads as
 * an unobtrusive status tag, not as a chrome element competing with the
 * title. inline-flex centers the dot/icon glyph against the cap-height
 * of the label instead of the x-height — which is what vertical-align:
 * middle (the old wrapping-text recipe) was approximating.
 *
 * Mobile: drops back into the 2x2 grid as the bottom-right cell, with
 * the original block-display + vertical-align hacks preserved so the
 * touch-target case is unchanged.
 */
const StatePill = styled(Box)<{
  $tone: "now" | "next" | "idle";
  $bottomPlaylist?: boolean;
}>`
  align-items: center;
  color: ${({ $tone }) =>
    $tone === "now"
      ? "#ffffff"
      : $tone === "next"
      ? "var(--pill-text, var(--accent, #3C87D2))"
      : "rgba(255, 255, 255, 0.55)"};
  display: inline-flex;
  font-size: 7px;
  font-weight: 800;
  gap: 3px;
  letter-spacing: 0.4px;
  line-height: 1;
  position: absolute;
  right: 6px;
  text-transform: uppercase;
  top: 4px;
  white-space: nowrap;

  /* Bottom-playlist variant: not used. The bottom-playlist row owns
     a dedicated <BPStatusColumn> as the row's third flex column —
     dot/icon + stacked label + duration all live there. Keeping the
     legacy StatePill stripped of bottom-playlist branching avoids
     piling per-variant overrides on top of the absolute-positioned
     side-rail badge. */

  /* Mobile: state pill drops into the BOTTOM-RIGHT grid cell, beneath
     the meta row. Reverts to the legacy block-display + vertical-align
     hacks for the icon/dot so the touch-target styling stays the same
     as before. align-self: start keeps it flush against the meta row
     above; the small margin-top adds breathing room below the meta
     baseline (the Row's mobile row-gap:1px would crowd the pill
     otherwise). */
  @media (max-width: 560px) {
    align-self: start;
    display: block;
    font-size: 8px;
    gap: 0;
    grid-column: 2;
    grid-row: 2;
    justify-self: start;
    line-height: 10px;
    margin-top: 5px;
    position: static;
    text-align: left;
  }
`;

/**
 * Bottom-playlist row status column.
 *
 * In the bottom-playlist variant the row is a 3-column flex
 *   [ Headshot | ContentArea (title + meta) | BPStatusColumn ]
 * and the status column on the right owns its own real estate — no
 * absolute positioning, no padding-right hacks on the title/meta
 * column to clear it. Internally it stacks:
 *
 *   [ ●  NOW    ]    ←— BPStatusTop: icon directly beside top word
 *   [    PLAY.. ]
 *   [   00:20   ]    ←— BPStatusDuration: chalk-tinted, centered
 *
 * Used only when `isBottomPlaylist` is true; the side-rail layout
 * still uses the legacy absolute-positioned <StatePill> + hidden
 * <DurationText> in row chrome.
 */
const BPStatusColumn = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  /* Fixed width so the title/meta column has a predictable budget to
     ellipsize against. Tuned to fit the widest label combo
     ("Now Playing" stacked) + the dot prefix without wrapping the
     label itself onto a 3rd line. */
  width: 96px;
`;

/** Top cluster wrapper. Just a centering anchor for the label; the dot
 *  and the play glyph live INLINE inside the label itself (see
 *  <BPStatusLabel>) so they sit on the same text baseline as "NOW" /
 *  "UP" with no flex cell mediating. */
const BPStatusTop = styled.div`
  display: block;
  line-height: 0.95;
  text-align: center;
`;

const BPStatusLabel = styled.span<{ $tone: "now" | "next" | "idle" }>`
  color: ${({ $tone }) =>
    $tone === "now"
      ? "#ffffff"
      : $tone === "next"
      ? "var(--pill-text, var(--accent, #3C87D2))"
      : "rgba(255, 255, 255, 0.55)"};
  /* inline-block so line-height applies to the wrapped <br> stack
     reliably across browsers — as a plain inline span the parent
     <BPStatusTop>'s default line-height was overriding ours and the
     two words stayed airy. */
  display: inline-block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  /* Tight line-height pulls "PLAYING" / "NEXT" up close under the
     leading word so the two-line label reads as a single stacked
     badge instead of two separate words with whitespace between them. */
  line-height: 0.95;
  text-align: center;
  text-transform: uppercase;
`;

const BPStatusDot = styled.span`
  background: #ff3b30;
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  margin-right: 4px;
  /* vertical-align: middle lands at the x-height of lowercase glyphs,
     which reads HIGH against an all-caps word like "NOW". Bumping the
     dot down a couple px via margin-bottom shifts its center to the
     cap-height midline — visually aligned with the optical center of
     NOW. Quick + dirty, no wrapper needed. */
  margin-bottom: 2px;
  vertical-align: middle;
  width: 8px;
`;

const BPPlayGlyph = styled.span`
  display: inline-block;
  margin-right: 4px;
  /* Same cap-height alignment nudge as <BPStatusDot> so the play
     triangle sits at the optical center of "UP" instead of riding the
     x-height. */
  margin-bottom: 2px;
  vertical-align: middle;
`;

const BPStatusDuration = styled.span`
  /* Chalk (#8fa9af) — same supporting-text register as MetaRow meta
     text and the Show: header label, so the duration reads as a
     stat-tag beneath the status label instead of competing with it
     as another bright glyph. */
  color: #8fa9af;
  font-feature-settings: "tnum" 1;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-align: center;
`;

const NowDot = styled.span<{ $bottomPlaylist?: boolean }>`
  /* Red across every variant — publisher review wanted the "live"
     cue to read as a universal record-light glyph, not a brand-tinted
     dot that changes hue per campaign accent. */
  background: #ff3b30;
  border-radius: 50%;
  display: inline-block;
  height: 4px;
  width: 4px;

  /* Bottom-playlist variant: not used — see <BPStatusDot>. */

  /* Mobile: pill is display:block on mobile so the dot rides the
     inline text baseline. vertical-align: middle lands at the
     x-height of lowercase glyphs which reads HIGH against an all-caps
     label — bump the dot down a couple px (via margin-bottom on the
     inline-block) so its visual center matches the cap-height midline
     of NOW PLAYING. Sized up a tick so it carries as a status glyph
     at the mobile pill scale. Same recipe as <BPStatusDot> in the
     bottom-playlist column. */
  @media (max-width: 560px) {
    height: 6px;
    width: 6px;
    margin-right: 3px;
    margin-bottom: 2px;
    vertical-align: middle;
  }
`;

/**
 * Inline wrapper for SVG icons inside the StatePill. On desktop the
 * pill is inline-flex with align-items:center so this is just an
 * inline-flex slot. On mobile it picks up the legacy vertical-align:
 * middle so the icon lines up with the first cap of the label.
 */
const InlineIconSlot = styled.span<{ $bottomPlaylist?: boolean }>`
  display: inline-flex;

  /* Bottom-playlist variant: not used — see <BPStatusIconSlot>. */

  /* Mobile: same cap-height midline nudge as <NowDot> so the play
     triangle lines up next to "UP NEXT" instead of riding the
     x-height of lowercase letters. */
  @media (max-width: 560px) {
    margin-right: 3px;
    margin-bottom: 2px;
    vertical-align: middle;
  }
`;

const DurationText = styled(Text)<{ $bottomPlaylist?: boolean }>`
  /* Desktop: hidden — the player chrome already shows current / total
     time, and the 226h iframe doesn't have the horizontal budget for
     a per-row duration label on top of the (now top-right pinned)
     state pill. Mobile keeps it. */
  display: none;

  /* Matches the idle StatePill text color so duration reads as
     supporting text in the same visual register as NOW PLAYING /
     UP NEXT labels at rest. */
  color: rgba(255, 255, 255, 0.55);
  font-feature-settings: "tnum" 1;
  font-size: 9px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  letter-spacing: 0.3px;

  /* Bottom-playlist variant: DurationText isn't rendered at all in
     this layout — the row's <BPStatusColumn> owns the duration. */

  /* Mobile: duration tucks into the BOTTOM-LEFT grid cell, centered
     horizontally under the avatar. align-self: start overrides the
     grid's end-alignment so duration sits flush at the top of row 2,
     directly under the avatar's bottom edge. */
  @media (max-width: 560px) {
    display: block;
    grid-column: 1;
    grid-row: 2;
    align-self: start;
    justify-self: center;
    text-align: center;
  }
`;

const formatViews = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${n}`;
};

const formatTime = (sec: number): string => {
  const safe = Math.max(0, Math.floor(sec));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

export const PlaylistRow = ({
  clip,
  index,
  isActive,
  isUpNext,
}: PlaylistRowProps) => {
  const setActiveClip = useSetRecoilState(BrandedActiveClipState);
  const setActiveIndex = useSetRecoilState(BrandedActiveClipIndexState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";
  const rowRef = useRef<HTMLDivElement | null>(null);

  const photo = getPlayerPhoto(clip);
  const playerName = getPlayerName(clip);
  const teamLogo = getTeamLogo(clip);
  const teamName = getTeamName(clip);
  const views = getViewCount(clip);
  const title = composeBrandedTitle(clip) || `${playerName}'s highlight`;
  const clipDurationSec = getClipLengthSec(clip);

  // Opponent matchup now ships as real clip properties — partner-api PR
  // #1057 added opponentTeamName / opponentTeamLogo to the HLTV ingest
  // pipeline. Fall through to the deterministic mock context only for
  // clips processed before that PR shipped, so the row never renders an
  // empty crest while the historical backfill catches up.
  const realOpponentName = getOpponentTeamName(clip);
  const realOpponentLogo = getOpponentTeamLogo(clip);
  const needsMockFallback = !realOpponentName;
  const mock = needsMockFallback ? buildMockMatchContext(clip) : null;
  const opponentName = realOpponentName || mock?.opponentTeamName || "";
  const opponentLogo = realOpponentLogo || mock?.opponentTeamLogo || "";

  // Real round number when partner-api has emitted CS_Round; mock fallback
  // otherwise so the badge slot is always populated.
  const round = getRoundNumber(clip) ?? mock?.roundNumber ?? null;

  // Combat tags (ACE / 4K / Clutch / Wallbang / etc) — surfaced only in
  // the bottom-playlist variant. The side-rail row is too narrow to
  // budget extra chips after the team-vs-team pill + ROUND. Capped at
  // 2 so the meta row stays scannable even when partner-api emits a
  // big bundle (multiple situationals + kill-count + headshot count).
  // getDisplayTags() already ranks ACE > 4K > 3K > 2K > Clutch >
  // No Scope > Wallbang > Quickscope > 1 Tap > Flashed >
  // All Headshots > N HS, so taking the first 2 always picks the most
  // hype context.
  const combatTags = isBottomPlaylist ? getDisplayTags(clip).slice(0, 2) : [];

  const handleClick = () => {
    setActiveClip(clip);
    setActiveIndex(index);
  };

  // Side-rail StatePill (legacy). Bottom-playlist uses the dedicated
  // <BPStatusColumn> rendered as a real row column further down — not
  // an absolute-positioned overlay.
  const renderSideRailState = () => {
    if (isActive) {
      return (
        <StatePill $tone="now">
          <NowDot />
          Now Playing
        </StatePill>
      );
    }
    if (isUpNext) {
      return (
        <StatePill $tone="next">
          <InlineIconSlot>
            <PlayIcon size={7} />
          </InlineIconSlot>
          Up Next
        </StatePill>
      );
    }
    // Idle rows in the side-rail render nothing in the state slot —
    // the row itself is clickable, so a per-row play affordance is
    // redundant.
    return null;
  };

  // Bottom-playlist row status column. Stacks dot/icon + two-line
  // label on top, chalk duration below. Active / Up Next / idle all
  // get a column (idle is duration-only so the row has a visual
  // anchor on the right that matches the live rows above it).
  const renderBottomPlaylistStatus = () => {
    const tone: "now" | "next" | "idle" = isActive
      ? "now"
      : isUpNext
      ? "next"
      : "idle";
    let top: JSX.Element | null = null;
    if (isActive) {
      top = (
        <BPStatusTop>
          <BPStatusLabel $tone="now">
            <BPStatusDot />
            Now
            <br />
            Playing
          </BPStatusLabel>
        </BPStatusTop>
      );
    } else if (isUpNext) {
      top = (
        <BPStatusTop>
          <BPStatusLabel $tone="next">
            <BPPlayGlyph>
              <PlayIcon size={10} />
            </BPPlayGlyph>
            Up
            <br />
            Next
          </BPStatusLabel>
        </BPStatusTop>
      );
    }
    return (
      <BPStatusColumn aria-label={tone === "now" ? "Now playing" : undefined}>
        {top}
        <BPStatusDuration>{formatTime(clipDurationSec)}</BPStatusDuration>
      </BPStatusColumn>
    );
  };

  return (
    <Row
      ref={rowRef}
      $active={isActive}
      $bottomPlaylist={isBottomPlaylist}
      onClick={handleClick}
    >
      <Headshot
        $src={photo || undefined}
        $active={isActive}
        $bottomPlaylist={isBottomPlaylist}
        aria-label={playerName || "Player"}
      >
        {!photo && <HeadshotFallback />}
      </Headshot>
      <ContentArea $bottomPlaylist={isBottomPlaylist}>
        <TitleText $bottomPlaylist={isBottomPlaylist}>{title}</TitleText>
        <MetaRow $bottomPlaylist={isBottomPlaylist}>
          {/* Order per Figma: 📈 viewCount views · [teamA vs teamB pill]
              · ROUND N. Events / series labels live in the intro
              overlay only — keeps the row dense and scannable. */}
          {/* Hide the views cluster entirely when the clip has zero
              recorded views — a "0 views" chip read as a data error to
              publisher reviewers, and the slot is more useful collapsed
              so the team-vs-team chip can take the freed horizontal
              budget. Once a clip earns its first view it pops in
              naturally without any state churn. */}
          {views > 0 && (
            <ViewsCluster $bottomPlaylist={isBottomPlaylist}>
              <ViewsGlyph>
                <ChartIcon size={10} />
              </ViewsGlyph>
              <Text>{formatViews(views)} views</Text>
            </ViewsCluster>
          )}
          <TeamPill $bottomPlaylist={isBottomPlaylist}>
            <TeamCrest name={teamName} logoUrl={teamLogo} size={10} />
            <VsInline>
              <VsBadge size={9} />
            </VsInline>
            <TeamCrest name={opponentName} logoUrl={opponentLogo} size={10} />
          </TeamPill>
          {round != null && (
            <RoundBadge $bottomPlaylist={isBottomPlaylist}>
              Round {round}
            </RoundBadge>
          )}
          {/* Combat tags (ACE / 4K / Clutch / Wallbang / …) — bottom-
              playlist only. ACE gets the gold-gradient match-history
              treatment; the rest take the neutral accent-tinted chip
              recipe matching ROUND / TeamPill. */}
          {combatTags.map((tag) =>
            tag === "ACE" ? (
              <AceTagChip key={tag}>{tag}</AceTagChip>
            ) : (
              <TagChip key={tag}>{tag}</TagChip>
            ),
          )}
        </MetaRow>
      </ContentArea>
      {/* Side-rail layout: StatePill is absolutely positioned to the
          row's top-right (Row has position: relative). DurationText
          stays hidden on desktop (the player chrome carries time) but
          drops into the mobile 2x2 grid's bottom-left cell.
          Bottom-playlist layout: <BPStatusColumn> renders as the row's
          third real flex column on the right — owns its own width,
          no absolute positioning, no clearance hacks on title/meta. */}
      {!isBottomPlaylist && renderSideRailState()}
      {!isBottomPlaylist && (
        <DurationText>{formatTime(clipDurationSec)}</DurationText>
      )}
      {isBottomPlaylist && renderBottomPlaylistStatus()}
    </Row>
  );
};
